import { useCallback, useEffect } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { defaultShapeTextParagraphs } from '@/lib/shapes/shape-text-frame'
import { getShapeById } from '../helpers/shape-list'
import type { ShapeManagerStateApi } from './use-shape-manager-state'

export function useShapeTextCommit(ctx: ShapeManagerStateApi) {
  const {
    editStartShapeTextParagraphsRef,
    editingShapeTextId,
    editingShapeTextIdRef,
    onShapeCommitted,
    pendingShapeTextCommitShapeIdRef,
    shapeTextChainProjectedRef,
    shapeTextCommitTimerRef,
    shapesRef,
  } = ctx

  useEffect(() => {
    editingShapeTextIdRef.current = editingShapeTextId
  }, [editingShapeTextId, editingShapeTextIdRef])

  const cloneShapeTextParagraphs = useCallback(
    (paragraphs: Paragraph[]): Paragraph[] => JSON.parse(JSON.stringify(paragraphs)) as Paragraph[],
    []
  )

  const cancelShapeTextCommit = useCallback(() => {
    if (shapeTextCommitTimerRef.current != null) {
      clearTimeout(shapeTextCommitTimerRef.current)
      shapeTextCommitTimerRef.current = null
    }

    pendingShapeTextCommitShapeIdRef.current = null
  }, [pendingShapeTextCommitShapeIdRef, shapeTextCommitTimerRef])

  /**
   * WMPCM-7778: commits the in-progress shape text edit against the edit-start baseline and
   * advances the baseline, so typing persists without exiting edit mode (reload/spread switch).
   */
  const commitShapeTextNow = useCallback(
    (shapeId: string) => {
      const shape = getShapeById(shapesRef.current, shapeId)

      if (!shape) return

      const baseline = editStartShapeTextParagraphsRef.current

      if (baseline == null) return

      const current = shape.paragraphs ?? defaultShapeTextParagraphs()

      // The projection flag, not just the diff — see `shapeTextChainProjectedRef`. A commit whose
      // before/after are equal still has to reach the owner, because the owner's chain
      // redistribution is what gives the DOWNSTREAM frame its history action and its enqueue.
      if (
        !shapeTextChainProjectedRef.current &&
        JSON.stringify(baseline) === JSON.stringify(current)
      )
        return

      shapeTextChainProjectedRef.current = false

      onShapeCommitted?.(shape, 'update', {
        before: { paragraphs: cloneShapeTextParagraphs(baseline) },
        after: { paragraphs: cloneShapeTextParagraphs(current) },
      })

      // Re-read rather than reusing `current`: a chain reflow triggered by this very commit writes
      // back through `updateShape` while we are inside the callback, so `current` is a pre-reflow
      // snapshot. Baselining on it would make the NEXT window's before/after read the reflow's own
      // forward push as if the user had deleted text — the signal home.tsx uses to decide whether
      // a reflow may pull content back upstream.
      const settled = getShapeById(shapesRef.current, shapeId)

      editStartShapeTextParagraphsRef.current = cloneShapeTextParagraphs(
        settled?.paragraphs ?? current
      )
    },
    [
      cloneShapeTextParagraphs,
      onShapeCommitted,
      editStartShapeTextParagraphsRef,
      shapeTextChainProjectedRef,
      shapesRef,
    ]
  )

  /**
   * Re-points the active edit session's baseline at the shape's CURRENT paragraphs, with no
   * commit/history side effect. Required after undo/redo applies a paragraphs change to the
   * shape currently being edited: `applyHistoryAction`'s 'update' case writes straight through
   * `updateShape`, bypassing `commitShapeTextNow` entirely, so the baseline is left describing
   * the PRE-undo state. The next `commitShapeTextNow` (e.g. from `flushPendingShapeTextCommit`
   * at the top of the next `handleUndo`/`handleRedo`) would then diff that stale baseline against
   * the just-reverted paragraphs, see a "change", and push it as a brand-new forward edit —
   * silently re-doing the undo one step later. A no-op unless `shapeId` is the shape currently
   * being edited.
   */
  const resyncShapeTextEditBaseline = useCallback(
    (shapeId: string) => {
      if (editingShapeTextIdRef.current !== shapeId) return
      const shape = getShapeById(shapesRef.current, shapeId)

      if (!shape) return
      editStartShapeTextParagraphsRef.current = cloneShapeTextParagraphs(
        shape.paragraphs ?? defaultShapeTextParagraphs()
      )
    },
    [cloneShapeTextParagraphs, editStartShapeTextParagraphsRef, editingShapeTextIdRef, shapesRef]
  )

  /**
   * Run any pending debounced shape-text commit immediately. The shape-side counterpart to
   * `TextManagerRef.flushPendingContentCommit`, and required before a chain hand-off moves the
   * caret out of a shape: the 500ms timer would otherwise fire against a stale edit-start baseline
   * after the editor has already moved on (WMPCM-9070).
   */
  const flushPendingShapeTextCommit = useCallback(() => {
    const pendingId =
      pendingShapeTextCommitShapeIdRef.current ?? editingShapeTextIdRef.current ?? null

    cancelShapeTextCommit()
    if (pendingId) commitShapeTextNow(pendingId)
  }, [
    cancelShapeTextCommit,
    commitShapeTextNow,
    editingShapeTextIdRef,
    pendingShapeTextCommitShapeIdRef,
  ])

  const scheduleShapeTextCommit = useCallback(
    (shapeId: string) => {
      if (shapeTextCommitTimerRef.current != null) {
        clearTimeout(shapeTextCommitTimerRef.current)
      }

      pendingShapeTextCommitShapeIdRef.current = shapeId
      shapeTextCommitTimerRef.current = setTimeout(() => {
        shapeTextCommitTimerRef.current = null
        pendingShapeTextCommitShapeIdRef.current = null
        commitShapeTextNow(shapeId)
      }, 500)
    },
    [commitShapeTextNow, pendingShapeTextCommitShapeIdRef, shapeTextCommitTimerRef]
  )

  useEffect(() => cancelShapeTextCommit, [cancelShapeTextCommit])

  return {
    cloneShapeTextParagraphs,
    cancelShapeTextCommit,
    commitShapeTextNow,
    resyncShapeTextEditBaseline,
    flushPendingShapeTextCommit,
    scheduleShapeTextCommit,
  }
}

export type ShapeTextCommitApi = ReturnType<typeof useShapeTextCommit>
